import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { Button, Row, Col, Container, Badge, Alert } from 'react-bootstrap';
import {
  FaArrowLeft,
  FaEdit,
  FaCheck,
  FaTimes,
  FaPrint,
  FaRegFilePdf,
  FaFileSignature,
} from 'react-icons/fa';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';
import { useReceita, IReceita } from '../../hooks/receita';
import Loading from '../../components/Loading';
import StatusBadge from '../../components/Receita/StatusBadge';
import MedicamentoLista from '../../components/Receita/MedicamentoLista';

import {
  Titulo,
  InfoCard,
  InfoRow,
  InfoLabel,
  InfoValue,
  ActionsBar,
  MedicamentosSection,
} from './styles';
import { formataCPF, formataTelefone, TIPO_USUARIO } from '../../utils/funcoes';
import getResponseErrors from '../../utils/getResponseErrors';
import LoginPrescreve from '../../components/Receita/LoginPrescrece';
import ConectaCertificado from '../../components/Receita/LoginPrescrece/ConectaCertificado';

const ReceitaDetalhe: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();
  const { user } = useAuth();
  const {
    buscarReceita,
    alterarStatus,
    loading: loadingReceita,
    assinarReceita,
    cancelarReceita,
  } = useReceita();

  const [receita, setReceita] = useState<IReceita | null>(null);
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showConectaCertificado, setShowConectaCertificado] = useState(false);

  const isMedico = user.UsuTipo === TIPO_USUARIO.MEDICO;
  const isAdminOuSupervisor = user.UsuTipo <= TIPO_USUARIO.SUPERVISOR;
  const canEdit =
    receita?.RecStatus === 'Criada' && (isMedico || isAdminOuSupervisor);
  const canEmitir =
    receita?.RecStatus === 'Criada' && (isMedico || isAdminOuSupervisor);
  const canCancelar =
    (receita?.RecStatus === 'Criada' || receita?.RecStatus === 'Emitida') &&
    (isMedico || isAdminOuSupervisor);

  useEffect(() => {
    if (id) {
      carregarReceita(Number(id));
    }
  }, [id]);

  const carregarReceita = useCallback(
    async (codigo: number) => {
      setLoading(true);

      try {
        const data = await buscarReceita(codigo);
        setReceita(data);
      } catch (error) {
        history.push('/receitas');
      } finally {
        setLoading(false);
      }
    },
    [buscarReceita, history, id],
  );

  const handleEmitir = useCallback(async () => {
    if (!receita) return;

    if (receita.medicamentos.length === 0) {
      addToast({
        type: 'error',
        title: 'Não é possível emitir receita sem medicamentos',
      });
      return;
    }

    if (
      !window.confirm(
        'Tem certeza que deseja emitir esta receita? Após emitida, não poderá mais ser editada.',
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const updated = await alterarStatus(receita.RecCodigo, 'Emitida');
      setReceita(updated);
    } finally {
      setLoading(false);
    }
  }, [addToast, alterarStatus, receita]);

  const handleCancelar = useCallback(async () => {
    if (!receita) return;

    if (!window.confirm('Tem certeza que deseja cancelar esta receita?')) {
      return;
    }

    setLoading(true);
    try {
      const updated = await cancelarReceita(receita.RecCodigo);
      setReceita(updated);
      addToast({ type: 'success', title: 'Receita cancelada' });
    } finally {
      setLoading(false);
    }
  }, [addToast, cancelarReceita, receita]);

  const handleVoltar = useCallback(() => {
    history.goBack();
  }, [history]);

  const formatarData = (data: Date | string): string => {
    try {
      return format(parseISO(data.toString()), 'dd/MM/yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const handleAssinar = useCallback(async () => {
    if (!receita) return;
    setShowLogin(false);
    setShowConectaCertificado(false);

    if (receita.medicamentos.length === 0) {
      addToast({
        type: 'error',
        title: 'Não é possível assinar receita sem medicamentos',
      });
      return;
    }

    if (
      !window.confirm(
        'Tem certeza que deseja assinar esta receita? Após assinatura, não poderá mais ser cancelada.',
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const updated = await assinarReceita(receita.RecCodigo);
      setReceita(updated);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      if (
        mensagemErro ===
        'Autenticação Prescreve necessária. Realize o login na Prescreve.'
      )
        setShowLogin(true);

      if (mensagemErro === 'Certificado desconectado')
        setShowConectaCertificado(true);
    } finally {
      setLoading(false);
    }
  }, [addToast, assinarReceita, receita]);

  if (!receita) {
    return (
      <Container fluid className="mt-2">
        {(loading || loadingReceita) && <Loading />}
      </Container>
    );
  }

  return (
    <Container fluid className="mt-2">
      {(loading || loadingReceita) && <Loading />}

      {showLogin && (
        <LoginPrescreve
          show
          onClose={() => setShowLogin(false)}
          onSave={handleAssinar}
        />
      )}

      {showConectaCertificado && (
        <ConectaCertificado
          show
          onClose={() => setShowConectaCertificado(false)}
          onSave={handleAssinar}
        />
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button variant="outline-secondary" onClick={handleVoltar}>
            <FaArrowLeft size={16} /> Voltar
          </Button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <StatusBadge status={receita.RecStatus} size="lg" />
        </div>
      </div>

      <Titulo>
        Receita #{receita.RecCodigo} -
        <Badge
          className="ml-2"
          variant={receita.RecControlado ? 'warning' : 'secondary'}
        >
          {receita.RecControlado ? 'Controlado' : 'Simples'}
        </Badge>
      </Titulo>

      {receita.RecStatus === 'Emitida' && (
        <Alert variant="success" className="mb-3">
          <strong>Receita Emitida</strong> - Esta receita foi emitida e não pode
          mais ser alterada.
        </Alert>
      )}

      {receita.RecStatus === 'Cancelada' && (
        <Alert variant="danger" className="mb-3">
          <strong>Receita Cancelada</strong> - Esta receita foi cancelada.
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <InfoCard title="Dados do Paciente">
            <h5>Paciente</h5>
            <InfoRow>
              <InfoLabel>Nome:</InfoLabel>
              <InfoValue>{receita.RecPacNome}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>CPF:</InfoLabel>
              <InfoValue>{formataCPF(receita.RecPacCPF || '')}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Telefone:</InfoLabel>
              <InfoValue>
                {formataTelefone(receita.RecPacTelefone || '')}
              </InfoValue>
            </InfoRow>

            {receita.RecPacEndereco && (
              <InfoRow>
                <InfoLabel>Endereço:</InfoLabel>
                <InfoValue>{receita.RecPacEndereco}</InfoValue>
              </InfoRow>
            )}
          </InfoCard>
        </Col>

        <Col md={6}>
          <InfoCard title="Dados do Médico">
            <h5>Médico</h5>
            <InfoRow>
              <InfoLabel>Nome:</InfoLabel>
              <InfoValue>{receita.RecUsuNome}</InfoValue>
            </InfoRow>
            {receita.RecUsuCRM && (
              <InfoRow>
                <InfoLabel>CRM:</InfoLabel>
                <InfoValue>{receita.RecUsuCRM}</InfoValue>
              </InfoRow>
            )}
            {receita.RecUsuEndereco && (
              <InfoRow>
                <InfoLabel>Endereço:</InfoLabel>
                <InfoValue>{receita.RecUsuEndereco}</InfoValue>
              </InfoRow>
            )}
          </InfoCard>
        </Col>
      </Row>

      {receita.RecInformacao && (
        <InfoCard title="Observações Gerais" className="mt-3">
          <InfoValue style={{ whiteSpace: 'pre-wrap' }}>
            {receita.RecInformacao}
          </InfoValue>
        </InfoCard>
      )}

      <MedicamentosSection>
        {receita.medicamentos.length === 0 ? (
          <div className="text-center text-muted py-4">
            <p className="mb-0">Nenhum medicamento adicionado</p>
          </div>
        ) : (
          <MedicamentoLista
            medicamentos={receita.medicamentos}
            receitaStatus={receita.RecStatus}
            receitaCodigo={receita.RecCodigo}
            podeEditar={false}
            onUpdateMedicamento={() => {
              // No-op in detail view
            }}
            onDeleteMedicamento={() => {
              // No-op in detail view
            }}
          />
        )}
      </MedicamentosSection>

      <div className="mt-3 text-muted small">
        <Row>
          <Col md={6}>
            <strong>Criada em:</strong> {formatarData(receita.created_at)}
          </Col>
          <Col md={6} className="text-md-right">
            <strong>Atualizada em:</strong> {formatarData(receita.updated_at)}
          </Col>
        </Row>
      </div>

      <ActionsBar>
        <Button variant="outline-secondary" onClick={handleVoltar}>
          <FaArrowLeft size={16} /> Voltar
        </Button>

        {!receita.arquivo_url && receita.RecStatus !== 'Cancelada' && (
          <a
            className="btn btn-outline-primary"
            href={`/receita/imprimir/${receita.RecCodigo}`}
            target="_blank"
            rel="noreferrer"
          >
            <FaPrint size={16} />{' '}
            {receita.RecStatus === 'Emitida' ? 'Imprimir' : 'Pré Visualizar'}
          </a>
        )}

        {receita.arquivo_url && (
          <a
            className="btn btn-primary"
            href={receita.arquivo_url}
            rel="noreferrer"
            target="_blank"
          >
            <FaRegFilePdf size={16} /> Ver Receita
          </a>
        )}

        {canEdit && (
          <a
            className="btn btn-primary"
            href={`/receita/editar/${receita.RecCodigo}`}
          >
            <FaEdit size={16} /> Editar
          </a>
        )}

        {!receita.arquivo_url && receita.RecStatus === 'Emitida' && (
          <Button
            variant="success"
            onClick={handleAssinar}
            disabled={loading || receita.medicamentos.length === 0}
          >
            <FaFileSignature size={16} /> Assinar
          </Button>
        )}

        {canEmitir && (
          <Button
            variant="success"
            onClick={handleEmitir}
            disabled={loading || receita.medicamentos.length === 0}
          >
            <FaCheck size={16} /> Emitir
          </Button>
        )}

        {canCancelar && (
          <Button
            variant="outline-danger"
            className="ml-4"
            onClick={handleCancelar}
            disabled={loading}
          >
            <FaTimes size={16} /> Cancelar
          </Button>
        )}
      </ActionsBar>
    </Container>
  );
};

export default ReceitaDetalhe;
